import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  createContactRequest,
  logInRequest,
  reacceptContactTermsRequest,
  resetPasswordRequest,
  signOutRequest,
  updateContactRequest,
} from "../requests/contact.requests";
import {
  fetchFilteredInvoicesRequest,
  fetchInvoiceDownloadLinkRequest,
  fetchInvoicesRequest,
} from "../requests/invoice.requests";
import {
  createNewAddressRequest,
  deleteAddressRequest,
  deleteSavedOrderRequest,
  editAddressRequest,
  editSavedOrderRequest,
  fetchAddressesRequest,
  fetchSavedOrdersRequest,
  saveOrderRequest,
} from "../requests/library.requests";
import { calculateOrderPriceRequest, createNewOrderRequest, parseCsvQuoteRequest } from "../requests/newOrder.requests";
import {
  cancelOrderBatchRequest,
  cancelOrderRequest,
  downloadOrdersReportRequest,
  fetchFilteredOrdersRequest,
  fetchOrdersRequest,
} from "../requests/order.requests";
import { checkoutNewOrderRequest, checkoutOrderRequest, paymentRequest } from "../requests/payment.request";

export const addSuccessMessage = (state, { payload }) => {
  state.successMessages = [
    ...state.successMessages,
    { key: payload.message, id: uuidv4(), ignoreTranslations: payload.ignoreTranslations ?? false },
  ];
};
export const addErrorMessage = (state, { payload }) => {
  const key = payload?.response?.data?.message || payload.message;
  state.errorMessages = [
    ...state.errorMessages,
    { key, id: uuidv4(), ignoreTranslations: payload.ignoreTranslations ?? false },
  ];
};

export const toastSlice = createSlice({
  name: "toast",
  initialState: {
    successMessages: [],
    errorMessages: [],
  },
  reducers: {
    removeSuccessMessage: (state, { payload }) => {
      state.successMessages = state.successMessages.filter(({ id }) => id !== payload);
    },
    removeErrorMessage: (state, { payload }) => {
      state.errorMessages = state.errorMessages.filter(({ id }) => id !== payload);
    },
  },
  extraReducers: {
    [fetchOrdersRequest.rejected]: addErrorMessage,
    [fetchFilteredOrdersRequest.rejected]: addErrorMessage,
    [fetchAddressesRequest.rejected]: addErrorMessage,
    [fetchSavedOrdersRequest.rejected]: addErrorMessage,
    [createNewAddressRequest.fulfilled]: addSuccessMessage,
    [createNewAddressRequest.rejected]: addErrorMessage,
    [deleteAddressRequest.fulfilled]: addSuccessMessage,
    [deleteAddressRequest.rejected]: addErrorMessage,
    [editAddressRequest.fulfilled]: addSuccessMessage,
    [editAddressRequest.rejected]: addErrorMessage,
    [updateContactRequest.fulfilled]: addSuccessMessage,
    [updateContactRequest.rejected]: addErrorMessage,
    [createNewOrderRequest.fulfilled]: addSuccessMessage,
    [createNewOrderRequest.rejected]: addErrorMessage,
    [calculateOrderPriceRequest.rejected]: addErrorMessage,
    [fetchInvoicesRequest.rejected]: addErrorMessage,
    [fetchFilteredInvoicesRequest.rejected]: addErrorMessage,
    [fetchInvoiceDownloadLinkRequest.rejected]: addErrorMessage,
    [createContactRequest.rejected]: addErrorMessage,
    [createContactRequest.fulfilled]: addSuccessMessage,
    [logInRequest.rejected]: (state, action) => {
      if (action.payload?.code === "auth/wrong-password") return;
      if (action.payload?.code === "auth/user-not-found") return;
      if (action.payload?.code === "auth/too-many-requests") {
        addErrorMessage(state, { payload: { message: "login.forms.loginForm.noAccessError" } });
      } else {
        const key = action.payload?.response?.data?.message || action.payload?.message;
        addErrorMessage(state, { payload: { message: key || "api:500.somethingWentWrong" } });
      }
    },
    [signOutRequest.rejected]: (state) => {
      addErrorMessage(state, { payload: { message: "api:500.somethingWentWrong" } });
    },
    [resetPasswordRequest.fulfilled]: (state) => {
      addSuccessMessage(state, { payload: { message: "login.forms.forgotPasswordForm.successfulResectionMessage" } });
    },
    [resetPasswordRequest.rejected]: (state, action) => {
      if (action.payload?.code === "auth/user-not-found") return;

      const key = action.payload?.response?.data?.message || action.payload?.message;
      addErrorMessage(state, { payload: { message: key || "api:500.somethingWentWrong" } });
    },
    [reacceptContactTermsRequest.fulfilled]: addSuccessMessage,
    [reacceptContactTermsRequest.rejected]: addErrorMessage,
    [saveOrderRequest.fulfilled]: addSuccessMessage,
    [saveOrderRequest.rejected]: addErrorMessage,
    [deleteSavedOrderRequest.fulfilled]: addSuccessMessage,
    [deleteSavedOrderRequest.rejected]: addErrorMessage,
    [editSavedOrderRequest.fulfilled]: addSuccessMessage,
    [editSavedOrderRequest.rejected]: addErrorMessage,
    [parseCsvQuoteRequest.rejected]: addErrorMessage,
    [cancelOrderRequest.fulfilled]: addSuccessMessage,
    [cancelOrderRequest.rejected]: addErrorMessage,
    [cancelOrderBatchRequest.fulfilled]: addSuccessMessage,
    [cancelOrderBatchRequest.rejected]: addErrorMessage,
    [downloadOrdersReportRequest.fulfilled]: addSuccessMessage,
    [downloadOrdersReportRequest.rejected]: addErrorMessage,
    [checkoutNewOrderRequest.rejected]: addErrorMessage,
    [paymentRequest.fulfilled]: (state) => {
      addSuccessMessage(state, { payload: { message: "api:200.completePayment" } });
    },
    [paymentRequest.rejected]: (state, action) => {
      addErrorMessage(state, { payload: { message: action.payload.message, ignoreTranslations: true } });
    },
    [checkoutOrderRequest.fulfilled]: (state, action) => {
      if (action?.payload?.message?.includes("202")) addSuccessMessage(state, action);
    },
    [checkoutOrderRequest.rejected]: addErrorMessage,
  },
});

export const { removeSuccessMessage, removeErrorMessage } = toastSlice.actions;

export const retrieveSuccessMessages = (state) => state.toast.successMessages;
export const retrieveErrorMessages = (state) => state.toast.errorMessages;

export default toastSlice.reducer;
